import React, { useContext } from "react";
import {
  makeStyles,
  Typography,
  Box,
  Paper,
  Chip,
} from "@material-ui/core";
import {
  ArrowBack as ArrowBackIcon,
  Lock as LockIcon,
} from "@material-ui/icons";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    background: theme.mode === "light" ? "#f8fafc" : "#0f172a",
    padding: "32px 24px 48px",
    maxWidth: 820,
    margin: "0 auto",
  },
  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    color: theme.mode === "light" ? "#64748b" : "#94a3b8",
    textDecoration: "none",
    fontSize: 13,
    fontWeight: 500,
    marginBottom: 20,
    transition: "color 0.15s",
    "&:hover": { color: "#2563eb" },
  },
  pageTitle: {
    fontWeight: 700,
    fontSize: 24,
    color: theme.mode === "light" ? "#0f172a" : "#f1f5f9",
    letterSpacing: "-0.02em",
    marginBottom: 6,
  },
  pageDesc: {
    fontSize: 14,
    color: theme.mode === "light" ? "#64748b" : "#94a3b8",
    marginBottom: 28,
    lineHeight: 1.6,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
    gap: 16,
  },
  card: {
    borderRadius: 16,
    border: `1px solid ${theme.mode === "light" ? "rgba(15,23,42,0.08)" : "rgba(255,255,255,0.08)"}`,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)",
    background: theme.mode === "light" ? "#ffffff" : "#1e293b",
    padding: "24px 16px 20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
    cursor: "pointer",
    textDecoration: "none",
    transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow: "0 8px 24px rgba(0,0,0,0.1), 0 20px 48px rgba(0,0,0,0.08)",
      borderColor: "#2563eb",
    },
  },
  cardDisabled: {
    borderRadius: 16,
    border: `1px solid ${theme.mode === "light" ? "rgba(15,23,42,0.06)" : "rgba(255,255,255,0.05)"}`,
    background: theme.mode === "light" ? "#f8fafc" : "#111827",
    padding: "24px 16px 20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
    opacity: 0.65,
    cursor: "default",
  },
  logoWrap: {
    width: 64,
    height: 64,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  logoText: {
    fontWeight: 800,
    fontSize: 22,
    letterSpacing: "-0.03em",
  },
  platformName: {
    fontWeight: 600,
    fontSize: 13,
    color: theme.mode === "light" ? "#0f172a" : "#f1f5f9",
    textAlign: "center",
  },
  comingSoon: {
    fontSize: 10,
    fontWeight: 600,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    background: theme.mode === "light" ? "#f1f5f9" : "#1e293b",
    border: `1px solid ${theme.mode === "light" ? "#e2e8f0" : "#334155"}`,
    padding: "2px 8px",
    borderRadius: 20,
  },
}));

const PLATFORMS = [
  {
    key: "hotmart",
    name: "Hotmart",
    to: "/hotmart",
    bg: "#e8282b",
    textColor: "#fff",
    abbr: "H",
    active: true,
  },
  {
    key: "kiwify",
    name: "Kiwify",
    bg: "#00b37e",
    textColor: "#fff",
    abbr: "K",
    active: false,
  },
  {
    key: "eduzz",
    name: "Eduzz",
    bg: "#f5a623",
    textColor: "#fff",
    abbr: "E",
    active: false,
  },
  {
    key: "monetizze",
    name: "Monetizze",
    bg: "#5cb85c",
    textColor: "#fff",
    abbr: "M",
    active: false,
  },
  {
    key: "braip",
    name: "Braip",
    bg: "#6d28d9",
    textColor: "#fff",
    abbr: "B",
    active: false,
  },
  {
    key: "yampi",
    name: "Yampi",
    bg: "#7c3aed",
    textColor: "#fff",
    abbr: "Y",
    active: false,
  },
  {
    key: "appmax",
    name: "Appmax",
    bg: "#7b2ff7",
    textColor: "#fff",
    abbr: "A",
    active: false,
  },
  {
    key: "cartpanda",
    name: "Cartpanda",
    bg: "#0078d4",
    textColor: "#fff",
    abbr: "CP",
    active: false,
  },
];

function PlatformCard({ platform }) {
  const classes = useStyles();

  const logo = (
    <Box
      className={classes.logoWrap}
      style={{ background: platform.bg }}
    >
      <Typography
        className={classes.logoText}
        style={{ color: platform.textColor }}
      >
        {platform.abbr}
      </Typography>
    </Box>
  );

  if (platform.active) {
    return (
      <RouterLink to={platform.to} className={classes.card}>
        {logo}
        <Typography className={classes.platformName}>{platform.name}</Typography>
      </RouterLink>
    );
  }

  return (
    <Box className={classes.cardDisabled}>
      {logo}
      <Typography className={classes.platformName}>{platform.name}</Typography>
      <Typography className={classes.comingSoon}>Em breve</Typography>
    </Box>
  );
}

export default function Plataformas() {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <RouterLink to="/configuracoes" className={classes.backBtn}>
        <ArrowBackIcon style={{ fontSize: 16 }} />
        Voltar para Configurações
      </RouterLink>

      <Typography className={classes.pageTitle}>Plataformas</Typography>
      <Typography className={classes.pageDesc}>
        Configure a plataforma de vendas do seu negócio para começar a sincronizar pedidos e carrinhos abandonados.
      </Typography>

      <div className={classes.grid}>
        {PLATFORMS.map((p) => (
          <PlatformCard key={p.key} platform={p} />
        ))}
      </div>
    </div>
  );
}
